import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'FilterBar' }

import { reactive, ref, computed, nextTick, watch, set, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
// Element's dropdown parts and time picker aren't globally registered. Import unaliased:
// the babel-plugin-component resolver derives the lib path from the binding name.
// Used in the template as <dropdown> / <dropdown-menu> / <dropdown-item> / <time-picker>.
import { Dropdown, DropdownMenu, DropdownItem, TimePicker } from 'element-ui'
import { diacriticIncludes } from '@/util/search'
import { useSavedFilters } from '@/composables/useSavedFilters'
import { openSavedViews } from '@/composables/useSavedViewsDrawer'
import type { SavedFilterRow } from '@/util/savedFiltersApi'

// `group`: optional heading; consecutive options sharing a group render under one header row.
interface FilterOption { label: string; value: string | number; group?: string; badge?: string }
interface FilterDef {
  key: string
  type: 'text' | 'select' | 'multiselect' | 'toggle' | 'daterange' | 'numberrange' | 'timerange' | 'selecttext'
  label: string
  placeholder?: string
  operatorLabel?: string
  options?: FilterOption[]
  primary?: boolean
  // multiselect only: quick presets ("Country: US (15)") that fill the selection with a set of
  // option values. `presetsLabel` names the dropdown button (default "Select by").
  presets?: Array<{ key: string; label: string; values: Array<string | number>; divided?: boolean }>
  presetsLabel?: string
  // multiselect only: render an "All" checkbox row above the list and give each `group` header a
  // tri-state checkbox that selects or clears every option under it. Both act on the visible
  // (filtered) options and add to the current selection rather than replacing it.
  selectAll?: boolean
  selectAllLabel?: string
  // numberrange only: show an "Unlimited" checkbox (value gets unlimited: true)
  allowUnlimited?: boolean
  // selecttext only: the option value that reveals the free-text input (e.g. a
  // "search by code" mode). Value shape is { mode, text }; text is only meaningful
  // when mode === textOption.
  textOption?: string | number
  // select only: the option value that reveals a companion date range inside this
  // popover (e.g. a "Custom range" period). `rangeKey` names the daterange def that
  // holds the dates; that def is normally `hidden` so it renders no chip of its own.
  rangeOption?: string | number
  rangeKey?: string
  // Participates in values (seeded, applied, cleared, summarized) but renders no chip.
  hidden?: boolean
}
interface DateRangeValue { mode: 'days' | 'range'; days: string; from: string; to: string }


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  // Filter schema (array of FilterDef).
  filters: { type: Array as () => FilterDef[], required: true },
  // Seed values keyed by filter key (read once on init).
  initialValue: { type: Object as () => Record<string, any>, default: () => ({}) },
  resultCount: { type: Number, default: null },
  perPage: { type: Number, default: null },
  perPageOptions: { type: Array as () => number[], default: () => [5, 10, 25, 50] },
  // Subtle refreshing indicator next to the result count (not a full-screen overlay).
  loading: { type: Boolean, default: false },
  // Chip-styled shortcut toggles rendered in the chip row (e.g. a "Today's bookings"
  // quick filter). Each: { key, label, active? }. Clicking emits 'quick-action' with the
  // key; the parent owns the active state and reflects it back via `active`.
  quickActions: { type: Array as () => Array<{ key: string; label: string; active?: boolean; end?: boolean; solid?: boolean }>, default: () => [] },
  // Opt-in: enables the saved-views chip and per-user filter persistence for this
  // page. The value is the stable view_key (see savedViewsRegistry). When set,
  // FilterBar becomes the source of the initial applied filter state and emits
  // exactly one initial `change` after resolving precedence — the page should let
  // that drive its first fetch rather than fetching in its own mounted hook.
  savedFiltersKey: { type: String, default: '' },
  // Opt-in: on phones the chip row collapses behind a filter button with an
  // active-filter count (the bookings mobile toolbar pattern). Pages that
  // already wrap the bar in their own mobile toolbar leave this off.
  mobileCollapsible: { type: Boolean, default: false },
  // Opaque page-owned state to store alongside the filters in a saved view (e.g.
  // the calendar's active-column selection/order). It is persisted under `_extra`
  // in the saved blob and handed back via the `restore-extra` event on apply; the
  // page restores it. FilterBar never interprets it. Deep-watched so changes
  // persist to the last-used view.
  extraSavedState: { type: Object, default: null },
  // Per-entity pages (one board per project, one results page per survey) pass the
  // entity the views belong to: { id, name? }. It is persisted under `_entity` in the
  // saved blob so the SavedViewsDrawer can open the right board, and presets saved for
  // another entity are hidden here. Rows with no `_entity` show for every entity.
  savedFiltersEntity: { type: Object as () => { id: string | number; name?: string } | null, default: null },
  // Opt-in sort chip: [{ key, label }] fields the page can sort by. The bar renders
  // the chip and popover; the page owns the value ({ key, dir }) and re-fetches on
  // update:sort. Persisted in saved views under _sort and restored via update:sort.
  sortOptions: { type: Array as () => Array<{ key: string; label: string }>, default: () => [] },
  sortValue: { type: Object as () => { key: string; dir: 'asc' | 'desc' } | null, default: null },
  // Pages whose table has sortable headers keep sort-options (so saved views still
  // carry _sort) and turn the chip off, so the sort control appears once.
  showSortChip: { type: Boolean, default: true },
  // Direction wording, e.g. { asc: 'A to Z', desc: 'Z to A' }; default ascending / descending.
  sortDirLabels: { type: Object as () => { asc: string; desc: string }, default: () => ({ asc: 'Ascending', desc: 'Descending' }) },
},
  emits: ["change", "update:perPage", "quick-action", "open-saved-views", "restore-extra", "update:sort"],
  setup(__props, { expose, emit }: { emit: ({
  (e: 'change', values: Record<string, any>): void
  (e: 'update:perPage', value: number): void
  (e: 'quick-action', key: string): void
  (e: 'open-saved-views'): void
  (e: 'restore-extra', extra: Record<string, any>): void
  (e: 'update:sort', value: { key: string; dir: 'asc' | 'desc' }): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





// Group settings drive the date-picker display only (presentational).
const group = JSON.parse(localStorage.getItem('group') || '{}')
// Element UI's date-picker validates firstDayOfWeek as 1–7 (Sunday = 7); 0 is invalid.
const firstDayOfWeek = group.first_day_of_week || 7
const dateDisplayFormat = computed(() => {
  let fmt = (group.date_format || 'MM-DD-YYYY').replaceAll('Y', 'y').replaceAll('D', 'd')
  return group.show_day_of_week ? 'ddd, ' + fmt : fmt
})
// Time-picker display follows the group's time format; emitted value-format stays 'HH:mm'.
const timeDisplayFormat = computed(() => (group.time_format === '12h' ? 'hh:mm A' : 'HH:mm'))
// Reformat a yyyy-MM-dd calendar date (wall-clock, no tz) into the group's date_format.
function formatGroupDate(iso: string): string {
  if (!iso) return ''
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [y, m, d] = parts
  return (group.date_format || 'YYYY-MM-DD').replace(/YYYY/g, y).replace(/MM/g, m).replace(/DD/g, d)
}

function emptyForType(def: FilterDef): any {
  if (def.type === 'toggle') return false
  if (def.type === 'daterange') return { mode: 'range', days: '', from: '', to: '' }
  if (def.type === 'numberrange') return def.allowUnlimited ? { from: '', to: '', unlimited: false } : { from: '', to: '' }
  if (def.type === 'timerange') return { from: '', to: '' }
  if (def.type === 'selecttext') return { mode: '', text: '' }
  if (def.type === 'multiselect') return []
  return ''
}
function isObjType(def: FilterDef): boolean {
  return def.type === 'daterange' || def.type === 'numberrange' || def.type === 'timerange' || def.type === 'selecttext'
}
function cloneVal(def: FilterDef, v: any): any {
  if (isObjType(def)) return { ...(v || emptyForType(def)) }
  if (def.type === 'multiselect') return Array.isArray(v) ? [...v] : []
  return v
}

const quickActionsBefore = computed(() => props.quickActions.filter(qa => !qa.end))
const quickActionsAfter = computed(() => props.quickActions.filter(qa => qa.end))

// Coerce an externally-provided value (URL seed or a saved view's stored value)
// into the internal shape for a filter def, filling gaps with the empty default.
function coerceProvided(def: FilterDef, provided: any): any {
  if (isObjType(def)) {
    const base = emptyForType(def)
    return provided ? { ...base, ...provided } : base
  }
  if (def.type === 'multiselect') return Array.isArray(provided) ? [...provided] : []
  // Toggles are strict booleans: saved views and URL params can hand back strings ('true', 'false')
  // or numbers, and the switch only shows on for true while the chip counted any truthy value.
  if (def.type === 'toggle') return provided === true || provided === 'true' || provided === 1 || provided === '1'
  return provided !== undefined && provided !== null ? provided : emptyForType(def)
}
function seedValue(def: FilterDef): any {
  return coerceProvided(def, props.initialValue ? props.initialValue[def.key] : undefined)
}

// Build the full objects FIRST, then make them reactive. Vue 2.7's reactive() (Object
// .defineProperty observer) can't track keys added after observation, so seeding onto an
// already-reactive {} would leave computeds like hasActiveFilters blind to later changes.
const committedInit: Record<string, any> = {}
const draftInit: Record<string, any> = {}
const msQueryInit: Record<string, string> = {}
for (const def of props.filters) {
  committedInit[def.key] = seedValue(def)
  draftInit[def.key] = cloneVal(def, committedInit[def.key])
  if (def.type === 'multiselect' || def.type === 'select') msQueryInit[def.key] = ''
}
// committed = applied values; draft = values edited inside a popover (committed on Apply).
const committed = reactive(committedInit)
const draft = reactive(draftInit)
// Per-filter option search text for multiselect and select (keys pre-seeded so Vue 2.7 tracks them).
const msQuery = reactive(msQueryInit)

// Filters that appear after mount (e.g. the UTM-gated source filter) — add reactively.
watch(() => props.filters.map(f => f.key).join('|'), () => {
  for (const def of props.filters) {
    if (!(def.key in committed)) set(committed, def.key, seedValue(def))
    if (!(def.key in draft)) set(draft, def.key, cloneVal(def, committed[def.key]))
    if ((def.type === 'multiselect' || def.type === 'select') && !(def.key in msQuery)) set(msQuery, def.key, '')
  }
})

const openKey = ref<string | null>(null)

// --- Sort chip ------------------------------------------------------------------
const sortOpen = ref(false)
const currentSort = computed<{ key: string; dir: 'asc' | 'desc' }>(() => {
  if (props.sortValue && props.sortValue.key) return { key: props.sortValue.key, dir: props.sortValue.dir === 'desc' ? 'desc' : 'asc' }
  const first = props.sortOptions[0]
  return { key: first ? first.key : '', dir: 'asc' }
})
const currentSortLabel = computed(() => {
  const opt = props.sortOptions.find(o => o.key === currentSort.value.key)
  return opt ? opt.label : ''
})
function setSort(key: string, dir: 'asc' | 'desc') {
  emit('update:sort', { key, dir })
}
function flipSort() {
  setSort(currentSort.value.key, currentSort.value.dir === 'asc' ? 'desc' : 'asc')
}
// A restored view carries _sort; hand it back to the page.
function restoreSort(values: Record<string, any> | null) {
  if (!values || props.sortOptions.length === 0) return
  const sv = values._sort
  if (sv && sv.key && props.sortOptions.some(o => o.key === sv.key)) {
    const dir: 'asc' | 'desc' = sv.dir === 'desc' ? 'desc' : 'asc'
    if (sv.key !== currentSort.value.key || dir !== currentSort.value.dir) emit('update:sort', { key: sv.key, dir })
  }
}
const moreOpen = ref(false)
// Secondary filters explicitly surfaced via "More filters" (shown even before active).
const revealed = ref<string[]>([])

const defByKey = computed(() => new Map(props.filters.map(f => [f.key, f])))
// Companion daterange def of a select whose rangeOption equals `v` (draft or committed).
function rangeDef(def: FilterDef, v: any): FilterDef | null {
  if (def.type !== 'select' || def.rangeOption === undefined || !def.rangeKey) return null
  if (v === '' || v == null || String(v) !== String(def.rangeOption)) return null
  const r = defByKey.value.get(def.rangeKey)
  return r && r.type === 'daterange' ? r : null
}
// Draft key whose date range body renders inside this popover, or '' for none.
function rangeDraftKey(def: FilterDef): string {
  if (def.type === 'daterange') return def.key
  const r = rangeDef(def, draft[def.key])
  return r && draft[r.key] ? r.key : ''
}
function dateRangeActive(v: any): boolean {
  if (!v) return false
  return v.mode === 'days' ? !!v.days : !!(v.from && v.to)
}
function dateRangeSummary(v: any): string {
  if (!v) return ''
  if (v.mode === 'days' && v.days) return 'Last ' + v.days + ' day' + (Number(v.days) === 1 ? '' : 's')
  if (v.mode === 'range' && v.from && v.to) return formatGroupDate(v.from) + ' – ' + formatGroupDate(v.to)
  return ''
}

function isActive(def: FilterDef): boolean {
  const v = committed[def.key]
  if (def.type === 'toggle') return !!v
  if (def.type === 'multiselect') return Array.isArray(v) && v.length > 0
  if (def.type === 'daterange') return dateRangeActive(v)
  if (def.type === 'select') {
    // A range-revealing option filters nothing until its dates are set.
    const r = rangeDef(def, v)
    if (r) return dateRangeActive(committed[r.key])
  }
  if (def.type === 'numberrange') {
    if (!v) return false
    if (def.allowUnlimited && v.unlimited) return true
    return v.from !== '' && v.from != null && v.to !== '' && v.to != null
  }
  if (def.type === 'timerange') return !!(v && v.from && v.to)
  if (def.type === 'selecttext') {
    if (!v || v.mode === '' || v.mode == null) return false
    // A text mode with no text entered filters nothing, so it's not "active".
    if (v.mode === def.textOption) return v.text !== '' && v.text != null
    return true
  }
  return v !== '' && v != null
}

const primaryFilters = computed(() => props.filters.filter(f => f.primary && !f.hidden))
const secondaryFilters = computed(() => props.filters.filter(f => !f.primary && !f.hidden))

// Chips that COULD be shown (primary + active/revealed secondary), before width capping.
const candidateChips = computed(() => [
  ...primaryFilters.value,
  ...secondaryFilters.value.filter(f => isActive(f) || revealed.value.includes(f.key)),
])

// Width cap so chips + per-page selector stay on one row; the rest fall into "More filters".
// maxVisibleChips is recomputed by measuring (see below); 9999 = no cap (initial / measuring).
const maxVisibleChips = ref(9999)
// Active filters (carry state) and filters the user explicitly revealed from "More filters" are
// never hidden by the width cap — only default (inactive, non-revealed) chips overflow.
const isPinned = (f: FilterDef) => isActive(f) || revealed.value.includes(f.key)
// Active and just-revealed chips stay visible so they can actually be used (setting
// a value happens on the chip); only inactive, non-revealed chips fold into "More
// filters". When the visible chips can't fit a narrow screen they wrap to a second
// line (see .filter-bar__chips) rather than overflowing the page.
const visibleChips = computed(() => {
  const cands = candidateChips.value
  if (maxVisibleChips.value >= cands.length) return cands
  let room = Math.max(0, maxVisibleChips.value - cands.filter(isPinned).length)
  const kept = new Set<string>()
  for (const f of cands) {
    if (isPinned(f)) { kept.add(f.key); continue }
    if (room > 0) { kept.add(f.key); room-- }
  }
  return cands.filter(f => kept.has(f.key))
})
const moreFilters = computed(() => {
  const visKeys = new Set(visibleChips.value.map(f => f.key))
  const overflow = candidateChips.value.filter(f => !visKeys.has(f.key))
  const neverShown = secondaryFilters.value.filter(f => !isActive(f) && !revealed.value.includes(f.key))
  return [...overflow, ...neverShown]
})
const hasActiveFilters = computed(() => props.filters.some(isActive))
const activeCount = computed(() => props.filters.filter(isActive).length)
const mobileOpen = ref(false)
function toggleMobile() {
  mobileOpen.value = !mobileOpen.value
  // The chip row was display:none while closed, so its widths were zero; measure again.
  if (mobileOpen.value) nextTick(() => scheduleRecompute())
}

// --- One-row overflow measurement ---------------------------------------------------------
// Render all candidate chips, measure them, then cap maxVisibleChips to how many fit beside the
// right cluster (results + actions + per-page). Keeps the bar on a single row at any width.
const rowEl = ref<HTMLElement | null>(null)
const chipsEl = ref<HTMLElement | null>(null)
const rightEl = ref<HTMLElement | null>(null)
function elWidth(el: HTMLElement | null): number {
  return el ? Math.ceil(el.getBoundingClientRect().width) : 0
}
let scheduled = false
function scheduleRecompute() {
  if (scheduled) return
  scheduled = true
  requestAnimationFrame(() => { scheduled = false; recomputeOverflow() })
}
async function recomputeOverflow() {
  maxVisibleChips.value = 9999
  await nextTick()
  const row = rowEl.value, chips = chipsEl.value
  if (!row || !chips) return
  const GAP = 8
  // Clamp to the viewport so an overflowing ancestor (e.g. a wide results table
  // that widens the page) can't inflate the measured width and defeat collapsing.
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : row.clientWidth
  const avail = Math.min(row.clientWidth, viewportW) - elWidth(rightEl.value) - 12
  let reserved = 0
  chips.querySelectorAll<HTMLElement>('[data-fb-qa]').forEach(el => { reserved += elWidth(el) + GAP })
  const moreEl = chips.querySelector<HTMLElement>('[data-fb-more]')
  reserved += (moreEl ? elWidth(moreEl) : 132) + GAP // reserve for "More filters" even if not shown yet
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-clear]')) + GAP
  // Saved-views chip is always shown first and must never be overflowed.
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-views]')) + GAP
  // The sort chip (when a page opts in) is always shown too.
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-sort]')) + GAP
  let used = 0, count = 0
  for (const el of Array.from(chips.querySelectorAll<HTMLElement>('[data-fb-chip]'))) {
    const w = elWidth(el) + GAP
    if (used + w > avail - reserved) break
    used += w; count++
  }
  maxVisibleChips.value = count
}
let ro: ResizeObserver | null = null
onMounted(() => {
  scheduleRecompute()
  if (typeof ResizeObserver !== 'undefined' && rowEl.value) {
    ro = new ResizeObserver(() => scheduleRecompute())
    ro.observe(rowEl.value)
  }
})
onBeforeUnmount(() => { if (ro) { ro.disconnect(); ro = null } })
watch(
  () => [props.filters, committed, revealed.value, props.resultCount, props.loading, props.perPage],
  () => scheduleRecompute(),
  { deep: true }
)

function optionLabel(def: FilterDef, value: any): string {
  const opt = (def.options || []).find(o => o.value === value)
  return opt ? opt.label : String(value)
}
function valueSummary(def: FilterDef): string {
  const v = committed[def.key]
  if (def.type === 'select') {
    const r = rangeDef(def, v)
    return r ? dateRangeSummary(committed[r.key]) || optionLabel(def, v) : optionLabel(def, v)
  }
  if (def.type === 'toggle') return v ? 'On' : 'Off'
  if (def.type === 'multiselect') {
    const arr = Array.isArray(v) ? v : []
    return arr.map(val => optionLabel(def, val)).join(', ')
  }
  if (def.type === 'daterange') return dateRangeSummary(v)
  if (def.type === 'numberrange') {
    if (def.allowUnlimited && v.unlimited) return 'Unlimited'
    if (v.from !== '' && v.from != null && v.to !== '' && v.to != null) return v.from + ' – ' + v.to
    return ''
  }
  if (def.type === 'timerange') {
    if (v.from && v.to) return v.from + ' – ' + v.to
    return ''
  }
  if (def.type === 'selecttext') {
    if (v.mode === def.textOption) return v.text ? '"' + v.text + '"' : ''
    return optionLabel(def, v.mode)
  }
  return v
}

// ---- Multi-select helpers ----
function isChecked(def: FilterDef, val: any): boolean {
  const arr = draft[def.key]
  return Array.isArray(arr) && arr.includes(val)
}
// A preset replaces the draft selection with its values; Apply still commits it.
function applyOptionPreset(def: FilterDef, preset: { values: Array<string | number> }) {
  draft[def.key] = [...preset.values]
}
function toggleMulti(def: FilterDef, val: any) {
  const arr = Array.isArray(draft[def.key]) ? draft[def.key] : []
  draft[def.key] = arr.includes(val) ? arr.filter((x: any) => x !== val) : [...arr, val]
}
// Tri-state for a set of option values: 'on' when every value is in the draft, 'mixed' when
// some are, 'off' when none.
function bulkState(def: FilterDef, values: Array<string | number>): 'on' | 'mixed' | 'off' {
  if (values.length === 0) return 'off'
  const n = values.filter(v => isChecked(def, v)).length
  return n === values.length ? 'on' : n > 0 ? 'mixed' : 'off'
}
// Values under one header, restricted to what the search box currently shows.
function groupValues(def: FilterDef, group: string): Array<string | number> {
  return filteredOptions(def).filter(o => o.group === group).map(o => o.value)
}
// Header or "All" click: 'on' clears those values, anything else adds them (union, no duplicates).
function toggleBulk(def: FilterDef, values: Array<string | number>) {
  const arr: any[] = Array.isArray(draft[def.key]) ? draft[def.key] : []
  if (bulkState(def, values) === 'on') {
    draft[def.key] = arr.filter(x => !values.includes(x))
  } else {
    draft[def.key] = [...arr, ...values.filter(v => !arr.includes(v))]
  }
}
function filteredOptions(def: FilterDef): FilterOption[] {
  const q = (msQuery[def.key] || '').toLowerCase()
  const opts = def.options || []
  if (!q) return opts
  // A group heading counts as searchable text so typing a country lists its groups.
  return opts.filter(o => diacriticIncludes(o.label, q) || (o.group ? diacriticIncludes(o.group, q) : false))
}

function snapshot(): Record<string, any> {
  const out: Record<string, any> = {}
  for (const def of props.filters) out[def.key] = cloneVal(def, committed[def.key])
  return out
}
// What gets persisted in a saved view: the filter snapshot plus the page size
// (_perPage) when this bar owns pagination. Page size is restored on apply; page
// position is not (apply always lands on page 1). The `change` event still carries
// only the filter snapshot, so pages never see _perPage as a filter.
function savedSnapshot(): Record<string, any> {
  const snap = snapshot()
  if (props.perPage != null) snap._perPage = props.perPage
  if (props.sortOptions.length > 0) snap._sort = { ...currentSort.value }
  if (props.extraSavedState != null) snap._extra = props.extraSavedState
  const ent = props.savedFiltersEntity
  if (ent && ent.id != null && ent.id !== '') {
    snap._entity = ent.name ? { id: ent.id, name: ent.name } : { id: ent.id }
  }
  return snap
}
// Hand a restored view's page-owned extra state back to the page to apply.
function restoreExtra(values: Record<string, any> | null) {
  if (values && values._extra != null) emit('restore-extra', values._extra)
}
function emitChange() {
  // Any apply/remove/clear reflows the chip row, which would leave an open "More filters"
  // popover detached from its anchor — close it.
  moreOpen.value = false
  const snap = snapshot()
  // Persist the user's current filters (+ page size) as their per-user "last used".
  if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot())
  emit('change', snap)
}

// --- Saved views (opt-in via savedFiltersKey) ------------------------------------------------
const saved = props.savedFiltersKey
  ? useSavedFilters(props.savedFiltersKey, () => (props.savedFiltersEntity ? props.savedFiltersEntity.id : null))
  : null
const viewsOpen = ref(false)
const savedReady = ref(false)
const savingPreset = ref(false)
const newViewName = ref('')
// The preset currently reflected on the page; cleared the moment the user edits a filter.
const appliedPresetId = ref<number | null>(null)
const savedPresets = computed<SavedFilterRow[]>(() => (saved ? saved.presets.value : []))
const currentViewLabel = computed(() => {
  if (appliedPresetId.value != null) {
    const p = savedPresets.value.find(x => x.id === appliedPresetId.value)
    if (p) return p.name
  }
  return 'Views'
})

const inst = getCurrentInstance()
// $route/$router are augmented onto the Vue prototype by vue-router 3 and aren't
// on the typed instance proxy, so reach them through an untyped view.
const vm = inst ? (inst.proxy as any) : null
function readViewParam(): number | null {
  const v = vm?.$route?.query?.view
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function stripViewParam() {
  const router = vm?.$router
  const route = vm?.$route
  if (!router || !route) return
  const q = { ...route.query }
  if (!('view' in q)) return
  delete q.view
  router.replace({ query: q }).catch(() => {})
}

// Seed committed + draft from an external values object (a saved view). Mirrors the
// initial seeding but imperative, without emitting.
function applyValues(values: Record<string, any> | null) {
  for (const def of props.filters) {
    committed[def.key] = coerceProvided(def, values ? values[def.key] : undefined)
    draft[def.key] = cloneVal(def, committed[def.key])
  }
}

// Restore the page size stored in a view (if any), leaving position to reset.
function restorePerPage(values: Record<string, any> | null) {
  if (!values) return
  const pp = values._perPage
  if (pp != null && pp !== props.perPage) emit('update:perPage', pp)
}

function applyPreset(p: SavedFilterRow) {
  applyValues(p.filters)
  restorePerPage(p.filters)
  restoreSort(p.filters)
  restoreExtra(p.filters)
  appliedPresetId.value = p.id
  viewsOpen.value = false
  emitChange()
}

async function onSavePreset() {
  const name = newViewName.value.trim()
  if (!name || !saved) return
  savingPreset.value = true
  try {
    const id = await saved.savePreset(name, savedSnapshot())
    if (id != null) appliedPresetId.value = id
    newViewName.value = ''
    viewsOpen.value = false
  } finally {
    savingPreset.value = false
  }
}

async function toggleDefault(p: SavedFilterRow) {
  if (!saved) return
  await saved.setDefault(p.id, !p.is_default)
}

async function onDeletePreset(p: SavedFilterRow) {
  if (!saved) return
  await saved.remove(p.id)
  if (appliedPresetId.value === p.id) appliedPresetId.value = null
}

function onManageViews() {
  viewsOpen.value = false
  // Open the global drawer directly so pages don't each need to relay this;
  // the emit stays for any page that wants custom handling.
  openSavedViews()
  emit('open-saved-views')
}

// Apply a preset when the ?view= param appears/changes while already mounted
// (e.g. opening a view from the SavedViewsDrawer on the page you're already on).
function applyViewParam(id: number) {
  if (!saved) return
  const preset = saved.presets.value.find((p) => p.id === id)
  if (!preset) { stripViewParam(); return }
  applyValues(preset.filters)
  restorePerPage(preset.filters)
  restoreExtra(preset.filters)
  appliedPresetId.value = preset.id
  stripViewParam()
  emitChange()
}
watch(
  () => vm?.$route?.query?.view,
  (v) => {
    // Only react once the initial mount resolution has run (savedReady), so this
    // doesn't double-fire with onMounted's own param handling.
    if (!savedReady.value) return
    if (v == null || v === '') return
    const n = Number(v)
    if (Number.isFinite(n)) applyViewParam(n)
  },
)

// Resolve precedence on mount and drive the page's initial fetch. When saved views
// are enabled, FilterBar owns the initial applied state — the page must not fetch in
// its own mounted hook (it would race / double-fetch).
onMounted(async () => {
  if (!saved) return
  const presetId = readViewParam()
  await saved.load()
  // Re-seed committed from the CURRENT initial-value before resolving precedence.
  // A page may populate its filter values in its own mounted() hook, which runs
  // AFTER this child's setup — so the committed state seeded at setup can be stale
  // (missing URL filters). props.initialValue is reactive and reflects the page's
  // latest values by now (we just awaited saved.load()).
  applyValues(props.initialValue)
  const urlHasFilters = props.filters.some(isActive)
  const urlValues = snapshot()
  const res = saved.resolve({ presetId, urlValues, urlHasFilters })
  if (res.values) applyValues(res.values)
  restorePerPage(res.values)
  restoreSort(res.values)
  restoreExtra(res.values)
  appliedPresetId.value = res.appliedPresetId
  // Don't let restoring loaded last-used trigger a redundant save-back. Prime with the
  // stored view (which includes _perPage) so the perPage watcher below no-ops on restore.
  saved.primeLastUsedBaseline(res.values || savedSnapshot())
  if (presetId != null) stripViewParam()
  savedReady.value = true
  // One initial change so the page runs its first fetch with the resolved filters.
  emit('change', snapshot())
})

// Page-size changes (via the per-page selector) also persist to the last-used view.
watch(
  () => props.perPage,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
)
// Sort changes persist to the last-used view too.
watch(
  () => props.sortValue,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
  { deep: true },
)
// Page-owned extra state (e.g. active columns) changes also persist to last-used.
watch(
  () => props.extraSavedState,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
  { deep: true },
)

function onPopoverToggle(def: FilterDef, visible: boolean) {
  // The draft persists across opens/closes — closing a popover without Apply keeps what was
  // typed, so values can be staged across several chips and applied together.
  if (visible) {
    openKey.value = def.key
  } else if (openKey.value === def.key) {
    openKey.value = null
  }
}

function applyFilter() {
  // Apply ALL staged drafts at once. Entering values in several chips then pressing Apply on
  // any one commits them together in a single request (mirrors the prior filter behavior).
  for (const def of props.filters) committed[def.key] = cloneVal(def, draft[def.key])
  appliedPresetId.value = null
  // Release focus from the Apply button (or any focused input) before the popover
  // closes; otherwise el-popover sets aria-hidden on an ancestor that still contains
  // the focused element, which the browser blocks as an a11y violation.
  if (typeof document !== 'undefined') (document.activeElement as HTMLElement | null)?.blur?.()
  emitChange()
  openKey.value = null
}

function removeFilter(def: FilterDef) {
  const r = rangeDef(def, committed[def.key])
  if (r) {
    committed[r.key] = emptyForType(r)
    draft[r.key] = emptyForType(r)
  }
  committed[def.key] = emptyForType(def)
  draft[def.key] = emptyForType(def)
  revealed.value = revealed.value.filter(k => k !== def.key)
  appliedPresetId.value = null
  openKey.value = null
  emitChange()
}

function onIconClick(def: FilterDef, e: Event) {
  // Active chip: the icon resets that filter and must NOT open the popover.
  if (isActive(def)) {
    e.stopPropagation()
    removeFilter(def)
  }
  // Inactive chip: let the click bubble so the popover opens.
}

function addFromMore(def: FilterDef) {
  if (!revealed.value.includes(def.key)) revealed.value = [...revealed.value, def.key]
  // Release focus from the clicked menu item before the popover closes, otherwise
  // el-popover sets aria-hidden on an ancestor that still contains the focused
  // element (blocked by the browser as an a11y violation).
  if (typeof document !== 'undefined') (document.activeElement as HTMLElement | null)?.blur?.()
  moreOpen.value = false
  nextTick(() => { openKey.value = def.key; onPopoverToggle(def, true) })
}

function clearAll() {
  for (const def of props.filters) {
    committed[def.key] = emptyForType(def)
    draft[def.key] = emptyForType(def)
  }
  revealed.value = []
  appliedPresetId.value = null
  openKey.value = null
  emitChange()
}

// Exposed so a parent can drive the bar imperatively (e.g. a page-level "Clear
// filters" button) instead of remounting via :key — remounting would re-run the
// saved-views precedence and re-apply the default/last-used, undoing the clear.
expose({ clearAll, applyValues })

return { __sfc: true,props, emit, group, firstDayOfWeek, dateDisplayFormat, timeDisplayFormat, formatGroupDate, emptyForType, isObjType, cloneVal, quickActionsBefore, quickActionsAfter, coerceProvided, seedValue, committedInit, draftInit, msQueryInit, committed, draft, msQuery, openKey, sortOpen, currentSort, currentSortLabel, setSort, flipSort, restoreSort, moreOpen, revealed, defByKey, rangeDef, rangeDraftKey, dateRangeActive, dateRangeSummary, isActive, primaryFilters, secondaryFilters, candidateChips, maxVisibleChips, isPinned, visibleChips, moreFilters, hasActiveFilters, activeCount, mobileOpen, toggleMobile, rowEl, chipsEl, rightEl, elWidth, scheduled, scheduleRecompute, recomputeOverflow, ro, optionLabel, valueSummary, isChecked, applyOptionPreset, toggleMulti, bulkState, groupValues, toggleBulk, filteredOptions, snapshot, savedSnapshot, restoreExtra, emitChange, saved, viewsOpen, savedReady, savingPreset, newViewName, appliedPresetId, savedPresets, currentViewLabel, inst, vm, readViewParam, stripViewParam, applyValues, restorePerPage, applyPreset, onSavePreset, toggleDefault, onDeletePreset, onManageViews, applyViewParam, onPopoverToggle, applyFilter, removeFilter, onIconClick, addFromMore, clearAll, Dropdown, DropdownMenu, DropdownItem, TimePicker }
}

})